import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
	ONE_LINE_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { PassId } from "../models/PassId";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";

interface UseTableDataProps {
	onDelete: (passId: PassId) => void;
}

export const useTableData = ({ onDelete }: UseTableDataProps) => {
	const columnDefs: ColDef<PassId>[] = [
		{
			field: "Name",
			headerName: "Name",
			flex: 1.5,
			sortable: true,
			filter: true,
		},
		{
			field: "StandName",
			headerName: "Stands",
			flex: 0.8,
			wrapText: false,
			autoHeight: false,
			cellClass: ONE_LINE_CELL_CLASS,
			tooltipValueGetter: (params) => params.value,
			valueGetter: (params) =>
				[params.data?.StandName, params.data?.ExtraStandNames]
					.filter(Boolean)
					.join(", "),
		},
		{
			field: "PassTypeName",
			headerName: "Pass Type",
			flex: 0.8,
		},
		{
			field: "PassShapeName",
			headerName: "Pass Shape",
			flex: 0.8,
		},
		{
			field: "ScheduledTonnage",
			headerName: "Aim Tons",
			width: 95,
			wrapText: false,
			autoHeight: false,
			cellClass: [
				ONE_LINE_CELL_CLASS,
				NUMBER_CELL_CLASS,
				NUMBER_CELL_ROOMY_CLASS,
			],
			valueFormatter: (params) =>
				params.value != null ? params.value.toFixed(1) : "",
		},
		activeColumnDef<PassId>(),
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<PassId>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as PassId);
								}}
								color="error"
								sx={{ p: 0.25 }}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
